import {
  osName,
  isMobile,
  isTablet,
  isDesktop,
  browserName,
  mobileVendor,
} from "react-device-detect";
import axios from "axios";
import moment from "moment";
import { KeyboardEvent } from "react";
import { AxiosResponse } from "axios";
import { Dispatch } from "@reduxjs/toolkit";
import { parseISO, format } from "date-fns";

import { showAlert } from "./alert";
import { getAccessToken } from "./session";
import { CmsLinks } from "../../redux/global.types";
import { setLoading } from "../../redux/global.slice";
import { setCmsLinks } from "../../redux/global.slice";
import { API_CONNECTION_SOFTWARE } from "../constants/enum";
import { Env, Paths, Module, EndPoint } from "../constants";
import { apiSuccessCode } from "../constants/axios.instance";
import { AnalyticsEvents, capturePosthogEvent } from "./analytics";

type DeviceInfo = {
  osName: string;
  browser: string;
  deviceType: string;
  deviceManufacturer: string;
};

export const FontFamily = (
  fontWeight: number,
  fontSize: number | string,
  lineHeight: number | string,
  fontStyle = "normal",
  fontFamily = `MrEavesXLSanOT`,
) => {
  let fontsize = "";
  let lineheight = "";

  if (typeof fontSize === "string") fontsize = fontSize;
  else fontsize = `${fontSize}px`;

  if (typeof lineHeight === "string") lineheight = lineHeight;
  else lineheight = `${lineHeight}px`;

  return `${fontStyle} ${fontWeight} ${fontsize}/${lineheight} ${fontFamily}`;
};

/**
 * Checks if an API call was successful based on status code
 * @param {number} code - Status code to check
 * @returns {boolean} - True if successful, false otherwise
 */
export const isAPISuccessful = (code: number) => {
  if (
    code === apiSuccessCode.success ||
    code === apiSuccessCode.ok ||
    code === apiSuccessCode.postApiSuccess
  )
    return true;
  else return false;
};

/**
 * Uploads a file to the provided pre-signed URL.
 * @param {File} file - The file to be uploaded.
 * @param {string} imageAPIUrl - The pre-signed URL for uploading the file.
 * @param {Function} callback - Callback function to execute after a successful upload.
 */
const fetchImageFromUrl =
  (file: File, imageAPIUrl: string, callback: Function) =>
  (dispatch: Dispatch<any>) => {
    if (!navigator.onLine) {
      showAlert(2, "No internet connection!");
      return;
    }

    let config = {
      method: "put",
      maxBodyLength: Infinity,
      url: imageAPIUrl,
      headers: {
        "Content-Type": file.type,
      },
      data: file,
    };

    axios
      .request(config)
      .then((response: AxiosResponse<any>) => {
        if (isAPISuccessful(response?.status)) {
          callback();
        }
        dispatch(setLoading(false));
      })
      .catch((error) => {
        dispatch(setLoading(false));
        showAlert(2, error?.message);
        console.error("Error fetching image API:", error);
      });
  };

/**
 * Generates a unique filename by appending a timestamp and a random string.
 * @param {File} file - The file object containing the original name.
 * @returns {string} - The newly formatted unique filename.
 */
const generateUniqueFileName = (file: File) => {
  const timestamp = Math.floor(Date.now() / 1000);
  const randomString = Math.random().toString(36).substring(2, 8); // Random alphanumeric string
  const extension = file.name.split(".").pop(); // Extract file extension
  const baseName = file.name.replace(/\s+/g, "_").replace(/\.[^/.]+$/, ""); // Remove spaces and extension

  return `${baseName}_${timestamp}_${randomString}.${extension}`;
};

/**
 * Retrieves a pre-signed URL for uploading a file and uploads the file upon success.
 * @param {File} file - The file to be uploaded.
 * @param {Function} callback - Callback function to execute after successful upload.
 */
export const getPreSignedUrl =
  (file: File, callback: Function) => (dispatch: Dispatch<any>) => {
    if (!navigator.onLine) {
      showAlert(2, "No internet connection!");
      return;
    }

    const fileType = file.type;
    const fileName = generateUniqueFileName(file);

    const token = getAccessToken();

    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${Env.BASE_URL}${EndPoint.presignedUrl}?filename=${fileName}&fileType=${fileType}`,
      headers: {
        accept: "application/json",
        platform: "1",
        "accept-language": "en",
        "X-API-Key": Env.API_KEY,
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    };

    dispatch(setLoading(true));

    axios(config)
      .then((response: AxiosResponse<any>) => {
        const { data } = response;
        if (isAPISuccessful(data.statusCode)) {
          const imageAPIUrl = data?.data?.url;
          const imagePath = data?.data?.filePath;

          dispatch(
            fetchImageFromUrl(file, imageAPIUrl, () => {
              callback(imagePath);
            }),
          );
        }
      })
      .catch((err) => {
        dispatch(setLoading(false));
        showAlert(2, err.message);
      });
  };

/**
 * Appends the base media upload URL to the provided image path.
 * @param {string} imagePath - The file path returned from the upload API.
 * @param {boolean} [isIPFS] - Optional flag to indicate if the image is stored on IPFS.
 * @returns {string} - The full URL for accessing the uploaded file.
 */
export const appendImageUrl = (imagePath: string, isIPFS?: Boolean): string => {
  if (!imagePath || imagePath === "null") return "";
  if (imagePath?.startsWith("http://") || imagePath?.startsWith("https://")) {
    return imagePath;
  }
  if (
    imagePath?.startsWith("databis") ||
    imagePath?.startsWith("databis/media")
  )
    return `${Env.IMAGE}${imagePath}`;
  else if (imagePath?.startsWith("blob:http://")) return imagePath;
  else if (
    !(
      imagePath?.startsWith("databis") ||
      imagePath?.startsWith("databis/media") ||
      imagePath?.startsWith("blob:http://")
    ) &&
    isIPFS
  )
    return `${Env.IPFS_GATEWAY_URL}${imagePath}`;
  else return imagePath;
};

// export const appendIPFSUrl = ()

export const handleFileUpload = async (
  file: File,
  dispatch: Dispatch<any>,
): Promise<string> => {
  try {
    return await new Promise((resolve, reject) => {
      dispatch(
        getPreSignedUrl(file, (data: string) => {
          resolve(data);
        }),
      );
    });
  } catch (error) {
    console.error("File upload failed", error);
    throw error;
  }
};

/**
 * @defintion function to upload function to S3
 * @param file which needs to be uploaded
 * @param callback function which will be called after getting success from API
 * @returns url of the S3 bucket
 */
export const uploadFile =
  (
    file: File,
    type: string,
    setFieldValue?: Function | any,
    name?: string | any,
  ) =>
  (dispatch: Dispatch<any>) => {
    if (!navigator.onLine) {
      showAlert(2, "No internet connection!");
      return;
    }

    const formdata = new FormData();
    formdata.append("type", type);
    formdata.append("file", file);

    const config = {
      method: "post",
      url: Env.BASE_URL + EndPoint.mediaUpload,
      headers: {
        "Content-Type": "multipart/form-data",
        platform: 1,
        timezone: new Date().valueOf(),
        "accept-language": "en",
        "Access-Control-Allow-Origin": "*",
        "X-API-Key": Env.API_KEY,
      },
      data: formdata,
    };

    dispatch(setLoading(true));

    axios(config)
      .then((response: AxiosResponse<any>) => {
        const { data } = response;
        if (data.statusCode === apiSuccessCode.success) {
          setFieldValue(
            name ? name : "profilePicture",
            `${Env.IMAGE}${data.data.image}`,
          );
        }

        setTimeout(() => {
          dispatch(setLoading(false));
        }, 1500);
      })
      .catch((err) => {
        dispatch(setLoading(false));
        showAlert(2, err.message);
      });
  };

export const zendeskLogin =
  (email: string, callback?: (token: string) => void) =>
  (dispatch: Dispatch<any>) => {
    if (!navigator.onLine) {
      showAlert(2, "No internet connection!");
      return;
    }

    dispatch(setLoading(true));

    const config = {
      method: "post",
      url: Env.BASE_URL + `${Module.manufacturer}${EndPoint.zendesk}`,
      headers: {
        "Content-Type": "application/json",
        platform: 1,
        timezone: new Date().valueOf(),
        "accept-language": "en",
        "Access-Control-Allow-Origin": "*",
        "X-API-Key": Env.API_KEY,
      },
      data: { email },
    };

    axios(config)
      .then((response: AxiosResponse<any>) => {
        const { data } = response;
        if (data.statusCode === apiSuccessCode.success) {
          const token = data?.data?.token;
          if (callback) callback(token);
          // dispatch(setLoading(false));
        }
      })
      .catch((err) => {
        capturePosthogEvent(AnalyticsEvents.HELP_CENTER_AUTO_LOGIN_FAILED, {
          email,
          error_message: err?.message ?? "zendesk_login_failed",
        });
        showAlert(2, err.message);
        dispatch(setLoading(false));
      });
  };

/** to get the initail of a string */
export function getInitial(name: string) {
  if (!name) return ""; // handle empty input

  // Trim the name and split it into words
  const words = name.trim().split(" ");

  if (words.length === 0) return ""; // handle names that only contain spaces

  // Get the first word's initial and convert it to uppercase
  const initial = words[0].charAt(0).toUpperCase();

  return initial;
}

/**
 * Disables the Space key for the input field.
 * @param event - The keyboard event.
 */
export const disableSpaceKey = (event: KeyboardEvent<HTMLDivElement>) => {
  if (event.key === " ") {
    event.preventDefault();
  }
};

/** to get the route from the permission object */
export const getRouteFromPermissionKey = (module: string): string => {
  switch (module) {
    case "analytics":
      return Paths.ANALYTICS;
    case "rewards_management":
      return Paths.REWARDS_MANAGEMENT;
    case "batches":
      return Paths.BATCHES;
    case "strains":
      return Paths.STRAINS;
    case "announcements":
      return Paths.ANNOUNCEMENTS;
    case "qr_code":
      return Paths.QR_CODE_PURCHASING;
    case "employees_management":
      return Paths.EMPLOYEEES_MANAGEMENT;
    case "company_profile":
      return Paths.COMPANY_PROFILE;
    case "api_connection":
      return Paths.API_CONNECTIONS;
    case "support":
      return Paths.SUPPORT;

    default:
      return Paths.ANALYTICS;
  }
};

/**
 * @definition Formats the date to 'MM/DD/YYYY' format
 * @param {string | Date} dateString - Date to be formatted
 * @returns {string} - Formatted date string
 */
export const FormatDate = (dateString: string | Date | number): string => {
  const date = new Date(dateString);
  const adjustedDate = new Date(
    date.getTime() - date.getTimezoneOffset() * 60 * 1000,
  );

  return moment(adjustedDate).utc().format("MM/DD/YYYY");
  // return moment(date).utc().format("MM/DD/YYYY");
};

/** to get the index of data  */
export const getIndexOfData = (
  currentPage: number,
  itemsPerPage: number,
  indexWithinPage: number,
): string => {
  const serialNumber = (currentPage - 1) * itemsPerPage + indexWithinPage;
  return serialNumber.toString().padStart(2, "0");
};

/** to convert the date into iso format*/
export const convertDate = (dateString: Date) => {
  const date = new Date(dateString);
  date.setDate(date.getDate() + 1);
  return date.toISOString();
};

/** to format the date object in date and time */
export const formatDateAndTime = (dateString: string) => {
  const dateObject = parseISO(dateString);
  const formattedDate = format(dateObject, "MM/dd/yyyy | hh:mma");
  return formattedDate;
};

/** to capatalize the first letter */
export const formatCapitalize = (status: string) => {
  if (!status) return "-";
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

export const formatUnixToDate = (timestamp: number) => {
  const date = new Date(timestamp * 1000); // Convert seconds to milliseconds
  return `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date
    .getDate()
    .toString()
    .padStart(2, "0")}/${date.getFullYear()}`;
};

/**
 * Checks if the provided ID matches the MongoDB ObjectId pattern.
 *
 * @param {string} id - The ID to be validated against the MongoDB ObjectId pattern.
 * @returns {boolean} `true` if the ID matches the MongoDB ObjectId pattern, otherwise `false`.
 */
export const isValidMongoId = (id: string) => {
  const mongoIdRegex = /^[a-f\d]{24}$/i;
  return mongoIdRegex.test(id);
};

export function getCroppedImgN(
  imageBase64: string,
  crop: any,
  fileName?: string,
): Promise<File | null> {
  return new Promise((resolve) => {
    const image = new Image();
    image.src = imageBase64;
    image.onload = () => {
      const canvas = document.createElement("canvas");
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      canvas.width = crop.width;
      canvas.height = crop.height;
      const ctx = canvas.getContext("2d");

      if (ctx) {
        ctx.drawImage(
          image,
          crop.x * scaleX,
          crop.y * scaleY,
          crop.width * scaleX,
          crop.height * scaleY,
          0,
          0,
          crop.width,
          crop.height,
        );

        // Convert the canvas to a Blob
        canvas.toBlob((blob) => {
          if (blob) {
            // Create a File from the Blob
            const file = new File(
              [blob],
              fileName ? fileName : "croppedImage.jpg",
              {
                type: "image/jpeg",
              },
            );
            resolve(file);
          } else {
            resolve(null);
          }
        }, "image/jpeg");
      } else {
        resolve(null);
      }
    };

    image.onerror = () => {
      resolve(null);
    };
  });
}

/** restricting the form while submitting on enter press */
export const handleKeyDown = (event: any) => {
  if (event.key === "Enter") {
    event.preventDefault();
  }
};

export const formatState = (stateValue: string) => {
  return stateValue
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Masks a blockchain address, keeping the first two and last six characters visible.
 */
export const formatAddress = (address: string): string => {
  if (!address) {
    return "";
  }

  if (address.length <= 8) {
    return address.toUpperCase();
  }

  const prefix = address.slice(0, 2).toUpperCase(); // Get first 2 characters
  const suffix = address.slice(-6).toUpperCase(); // Get last 6 characters
  return `${prefix}****${suffix}`;
};

/**
 * @function handleNumberInput
 * @description Handles number input to allow only numeric and control keys.
 * @param {Event} e - Event object
 */
export const handleNumberInput = (e: any) => {
  // Regular expression to allow only numbers and some control keys (e.g., Backspace, Arrow keys)
  const allowedKeys = /[0-9]|Backspace|ArrowLeft|ArrowRight|Delete|Tab/;

  // Prevent default behavior if the key pressed is not allowed
  if (!allowedKeys.test(e.key)) {
    e.preventDefault();
  }
};

/**
 * Checks the internet connection status and displays an alert if offline.
 *
 * @param {Function} showAlert - A callback function to display an alert.
 *        - `type` (number): Alert type (e.g., 2 for error).
 *        - `message` (string): The alert message to be displayed.
 * @returns {boolean} - Returns `true` if online, `false` if offline.
 */
export const checkInternetAndShowAlert = (
  showAlert: (type: number, message: string) => void,
): boolean => {
  // Check if the browser is offline
  if (!navigator.onLine) {
    // Trigger an alert to notify the user about connectivity issues
    showAlert(
      2,
      "We're experiencing technical difficulties. Please try again later.",
    );

    return false; // Return false to indicate no internet connection
  }

  return true; // Return true if internet is available
};

export const handleExternalUpdate = (value: string) => {
  if (value === API_CONNECTION_SOFTWARE.LIMS) return "Confident Lims";
  else if (value === API_CONNECTION_SOFTWARE.NORDIC_ANALYTICAL)
    return "Nordic Analytical";
  else return "Metrc";
};

/**
 * Rounds a numeric value (string or number) to two decimal places.
 *
 * @param {string | number} value - The value to round. Can be a number or a numeric string.
 * @returns {number} The number rounded to two decimal places. Returns NaN for invalid input.
 */
export const roundToTwo = (value: string | number): string => {
  const num = Number(value);

  // If conversion fails, return 'NaN'
  if (isNaN(num)) return "NaN";

  // Format to 2 decimal places as string
  return num.toFixed(2);
};

/**
 * Rounds a numeric value (string or number) to three decimal places.
 *
 * @param {string | number} value - The value to round. Can be a number or a numeric string.
 * @returns {number} The number rounded to three decimal places. Returns NaN for invalid input.
 */
export const roundToThree = (value: string | number) => {
  const num = Number(value);

  if (isNaN(num)) return NaN;

  // Round to 3 decimal places
  const rounded = num.toFixed(3);

  // Remove trailing zeros and optional decimal point
  return rounded.replace(/\.?0+$/, "");
};

/**
 * Formats a numeric value to a string with at most two decimal places.
 * Handles null, undefined, empty string, and invalid numbers by returning "0".
 * Pads with a trailing zero if there's only one decimal place.
 * @param {number | string} value - The numeric value to format
 * @returns {string} - The formatted price string
 */
export const formatPrice = (value: number | string): string => {
  if (value === null || value === undefined || value === "") return "0";

  const num = Number(value);

  if (Number.isNaN(num)) return "0";

  const [integerPart, decimalPart] = num.toString().split(".");

  // No decimal part → return as is
  if (!decimalPart) {
    return integerPart;
  }

  // One decimal digit → pad one more
  if (decimalPart.length === 1) {
    return `${integerPart}.${decimalPart}0`;
  }

  // Two or more decimals → take first two
  return `${integerPart}.${decimalPart.slice(0, 2)}`;
};

/**
 * Checks if the application is running in production environment
 * @returns {boolean} true if running in production, false otherwise
 */
export const isProduction = Env.MODE === "production";

/**
 * Creates a support ticket by making a POST request to the Zendesk API.
 * @param {object} payload - The payload to send in the request body.
 * @param {Function} callback - Callback function to execute after successful ticket creation.
 */
export const createSupportTicket =
  (payload: any, callback: Function) => (dispatch: Dispatch<any>) => {
    if (!navigator.onLine) {
      showAlert(2, "No internet connection!");
      return;
    }

    dispatch(setLoading(true));
    const token = getAccessToken();

    const config = {
      method: "post",
      url: Env.BASE_URL + Module.manufacturer + EndPoint.createZendeskTicket,
      headers: {
        platform: 1,
        "X-API-Key": Env.API_KEY,
        "accept-language": "en",
        Accept: "application/json",
        timezone: new Date().valueOf(),
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      data: JSON.stringify(payload),
    };

    axios(config)
      .then((response: AxiosResponse<any>) => {
        if (response.status === 201 || response.status === 200) {
          callback(response.data);
        } else {
          showAlert(2, "Failed to create support ticket.");
        }
        dispatch(setLoading(false));
      })
      .catch((err) => {
        dispatch(setLoading(false));
        showAlert(2, err.message);
      });
  };

export const getUserDeviceInfo = (): DeviceInfo => {
  let deviceType: DeviceInfo["deviceType"] = "";

  if (isMobile) {
    deviceType = "mobile";
  } else if (isTablet) {
    deviceType = "tablet";
  } else if (isDesktop) {
    deviceType = "desktop";
  }

  const deviceManufacturer = mobileVendor !== "none" ? mobileVendor : "";

  return {
    deviceType,
    deviceManufacturer,
    browser: browserName,
    osName: osName || "os_other",
  };
};

export const getPercentage = (num?: number, denom?: number): string => {
  if (num === undefined || denom === undefined || denom === 0) return "-";

  const percentage = (denom / num) * 100;

  if (Number.isInteger(percentage)) {
    return `${percentage}%`;
  }

  return `${percentage.toFixed(2)}%`;
};

export const redirectToZendesk =
  (jwtToken: string | null, returnTo?: string) => (dispatch: Dispatch<any>) => {
    if (!jwtToken) {
      console.error("Zendesk JWT token is missing");
      return;
    }

    dispatch(setLoading(true));

    // Create a form dynamically
    const form = document.createElement("form");
    form.method = "POST";
    form.action = Env.ZENDESK_JWT_URL; // your Zendesk subdomain

    // Required field: jwt
    const jwtInput = document.createElement("input");
    jwtInput.type = "hidden";
    jwtInput.name = "jwt";
    jwtInput.value = jwtToken;
    form.appendChild(jwtInput);

    // Optional but recommended: return_to
    if (returnTo) {
      const returnInput = document.createElement("input");
      returnInput.type = "hidden";
      returnInput.name = "return_to";
      returnInput.value = returnTo;
      form.appendChild(returnInput);
    }

    // Append and submit
    document.body.appendChild(form);
    form.submit();
  };

//  for cms links
export const getCmsLinks = () => (dispatch: Dispatch<any>) => {
  if (!navigator.onLine) {
    showAlert(2, "No internet connection!");
    return;
  }

  dispatch(setLoading(true));
  const token = getAccessToken();
  const config = {
    method: "get",
    url: Env.BASE_URL + Module.manufacturer + EndPoint.cmsContent,
    headers: {
      platform: 1,
      "X-API-Key": Env.API_KEY,
      "accept-language": "en",
      Accept: "application/json",
      timezone: new Date().valueOf(),
      "Content-Type": "application/json",
      "Access-Control-Allow-Origin": "*",
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  };

  axios(config)
    .then((response: AxiosResponse<any>) => {
      if (isAPISuccessful(response.data.statusCode)) {
        const cmsArray = response?.data?.data; //array
        const formattedCms: CmsLinks = cmsArray.reduce(
          (acc: CmsLinks, item: any) => {
            if (item.type && item.content) {
              acc[item.type as keyof CmsLinks] = item.content;
            }
            return acc;
          },
          {
            privacy_policy: "",
            terms_of_use: "",
            your_privacy_choices: "",
          },
        );

        dispatch(setCmsLinks(formattedCms));
      }
      dispatch(setLoading(false));
    })
    .catch((err) => {
      dispatch(setLoading(false));
      showAlert(2, err.message);
    });
};

/** Splits pipe-delimited CSV values into trimmed items (e.g. "A | B" → ["A", "B"]). */
export const parsePipeDelimitedValues = (
  value: string | string[] | null | undefined,
): string[] => {
  if (value == null) return [];

  const items = Array.isArray(value) ? value : [String(value)];

  return items
    .flatMap((item) => String(item).split("|"))
    .map((item) => item.trim())
    .filter(Boolean);
};

/** Normalizes strain description newlines for HTML display. */
export const formatStrainDescriptionHtml = (description: string): string => {
  const normalized = description
    .replace(/\\n/g, "\n")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n");

  return normalized.replace(/\n/g, "<br />");
};

/** Formats upload preview cells so line breaks and pipe-delimited values are visible. */
export const formatStrainUploadPreviewCell = (cell: unknown): string => {
  const text = String(cell ?? "");

  if (text.includes("\n")) {
    return text;
  }

  if (text.includes("|")) {
    return parsePipeDelimitedValues(text).join("\n");
  }

  return text;
};

/** Truncates a number to a fixed number of decimal places without rounding. */
export const truncateToDecimals = (
  num: number,
  decimalPlaces: number = 2,
): number => {
  const multiplier = Math.pow(10, decimalPlaces);
  return Math.floor((num + Number.EPSILON) * multiplier) / multiplier;
};

export const SIDEBAR_ORDER = [
  "analytics",
  "batches",
  "strains",
  "employees_management",
  "rewards_management",
  "qr_code",
  "announcements",
  "company_profile",
  "api_connection",
] as const;

/**
 * Calculates the correct landing page route for a user based on type, subscription, and permissions.
 */
export const getLandingPageRoute = (
  userType: string,
  permissions: any,
  hasSubscription: boolean,
): string => {
  if (userType === "MANUFACTURER_USER" && hasSubscription === false) {
    return Paths.SUBSCRIPTION;
  }

  if (
    userType === "MANUFACTURER_USER" ||
    permissions?.analytics?.view === true
  ) {
    return Paths.ANALYTICS;
  }

  if (permissions) {
    const firstTrueViewKey = SIDEBAR_ORDER.find(
      (key) => permissions[key]?.view === true,
    );
    if (firstTrueViewKey) {
      return getRouteFromPermissionKey(firstTrueViewKey);
    }
  }

  return Paths.ACCESS_DENIED;
};
